import { ConfirmAndCancelButtons, TextOverflowDiv } from '@components/common/layoutUtils';
import { commonPopupCSS } from '@utils/popupUtils';
import styled, { css } from 'styled-components';
import { isTouchScreen } from '@utils/screenUtils';
import { CELL_FONT_SIZE, CELL_LINE_HEIGHT, CELL_PADDING_Y, TASK_ROW_HEIGHT } from './rowMetrics';

/* A field you can change shows as one under the mouse: a soft rounded box in the accent, inside the cell, kept while
   its picker is open - a popup in the cell (course, category, source, pages, still need), the date pickers' field
   while their calendar is out (due, start ahead), or a dropdown's trigger (priority). Also on the repeat list's rows. */
export const editableCellHoverCSS = css`
  .td.editable {
    cursor: pointer;
  }
  .td.editable:hover::after,
  .td.editable:has(.popup-content, .react-datepicker-ignore-onclickoutside, [aria-expanded='true'])::after {
    content: '';
    position: absolute;
    inset: 6px 3px;
    border-radius: 6px;
    box-shadow: inset 0 0 0 1px ${props => `color-mix(in srgb, ${props.theme.primary} 35%, ${props.theme.background})`};
    background: ${props => `color-mix(in srgb, ${props.theme.primary} 5%, transparent)`};
    pointer-events: none;
  }
  /* Open, its edge a step stronger - and it stands in for the frames the cell and the date fields drew around a field
     being edited. */
  .td.editable:has(.popup-content, .react-datepicker-ignore-onclickoutside, [aria-expanded='true'])::after {
    box-shadow: inset 0 0 0 1px ${props => `color-mix(in srgb, ${props.theme.primary} 65%, ${props.theme.background})`};
  }
  .td.editable:has(:focus-visible):not(:focus-visible),
  .td.editable .react-datepicker-wrapper {
    box-shadow: none;
    border-color: transparent !important;
  }
`;

export const TableButtons = styled(ConfirmAndCancelButtons)`
  justify-content: flex-start;
  margin: 10px 20px 30px;
  /* "+ Add new task" stays put while the columns scroll sideways, as the task names do. */
  position: sticky;
  left: 20px;
  width: fit-content;
`;

// A row: a soft tint of the accent on hover and when selected (no hand cursor - only the title and the controls are
// clickable), separators between rows and no vertical grid. The select box, the title's ✎ and +, and ⋯ show on hover;
// the select boxes stay out once anything is selected, and on touch screens everything stays out.
export const TableRow = styled.div<{ selected?: boolean; isPreview?: boolean }>`
  display: flex;
  position: relative;
  &.last {
    .td {
      border-bottom: 1px solid ${props => rowLine(props.theme)};
    }
    + ${TableButtons} {
      margin-top: 10px;
    }
  }
  &.closed {
    + ${TableButtons} {
      display: none;
    }
  }
  .course-category-picker-content {
    top: 0 !important;
    ${commonPopupCSS}
  }
  .td {
    flex-shrink: 0;
    /* Exactly the height the list places each row at, whatever a browser's font rounding makes of the content. */
    height: ${TASK_ROW_HEIGHT}px;
    border: 1px solid transparent;
    border-top-color: ${props => rowLine(props.theme)};
    /* The cell's frame marks keyboard focus and a field being edited - not a button just clicked. */
    &:focus-visible,
    &:not(.taskId):not(.complete):has(:focus-visible) {
      box-shadow: inset 0 0 0 1px ${props => props.theme.primary};
    }
    &:focus {
      outline: none;
    }
    color: ${props => props.theme.textColor};
    font-size: ${CELL_FONT_SIZE}px;
    line-height: ${CELL_LINE_HEIGHT}px;
    position: relative;
    vertical-align: top;
    background: ${props =>
      props.selected ? `color-mix(in srgb, ${props.theme.primary} 10%, ${props.theme.background})` : props.theme.background};
    box-sizing: border-box;
    ${TextOverflowDiv} {
      padding: ${CELL_PADDING_Y}px 9px;
      height: 100%;
      box-sizing: border-box;
    }
    /* Semibold at 12.5 px (Petr's pick of four): lighter or smaller read as disabled. The title leads by size and colour. */
    *:not(i) {
      font-weight: 600 !important;
    }
    .cell-popup-content {
      ${commonPopupCSS};
      top: 0 !important;
      left: 0 !important;
      min-height: 100%;
      min-width: 100%;
    }
    /* Subtasks open under the row, not over the task they belong to (Petr), and over the rows below. */
    .cell-popup-content.subtasks-popup {
      top: calc(100% + 2px) !important;
      min-height: 0;
    }
    &:has(.cell-popup-content.subtasks-popup) {
      z-index: 8 !important;
    }
    .react-datepicker-wrapper {
      /* Block boxes all the way down: as inline boxes on the text baseline they left a sliver below the field, and the
         row grew taller than the list places it. */
      display: block;
      * {
        background-color: transparent !important;
      }
      .react-datepicker__input-container,
      .react-datepicker__input-container input {
        display: block;
      }
      .react-datepicker__input-container input {
        box-shadow: none;
        width: 100% !important;
        font-size: ${CELL_FONT_SIZE}px !important;
        line-height: ${CELL_LINE_HEIGHT}px;
        color: inherit;
        padding: ${CELL_PADDING_Y}px 9px;
      }
    }
  }
  .td.taskId > *,
  .title-hover-action,
  .row-actions button {
    ${!isTouchScreen && 'visibility: hidden;'}
  }
  .row-actions button.open {
    visibility: visible;
  }
  /* Hover shows and hides at once: the select box's own 0.2 s transition kept it on screen after the mouse left, so a
     quick sweep down the list left a trail of boxes (Petr: "laggy"); the row tint switches at once for the same reason. */
  .td.taskId * {
    transition: none;
  }
  ${props =>
    props.selected &&
    `
    .td.taskId > * {
      visibility: visible;
    }
  `}
  .selecting & .td.taskId > * {
    visibility: visible;
  }
  &:hover {
    .td {
      background: ${props =>
        `color-mix(in srgb, ${props.theme.primary} ${props.selected ? 13 : 5}%, ${props.theme.background})`};
    }
    .td.taskId > *,
    .title-hover-action,
    .row-actions button,
    i.delete-button-icon,
    button {
      visibility: visible;
    }
  }
  &.completed .td {
    color: ${props => props.theme.textColor};
  }
  ${editableCellHoverCSS};
  .td.actions {
    .popup-content span {
      white-space: nowrap;
    }
    .delete-button {
      padding: 0;
    }
  }
  .td.details {
    padding: 0;
  }
  .td.title {
    font-size: 13px;
    line-height: ${CELL_LINE_HEIGHT}px;
    color: ${props => props.theme.textStrongColor};
    /* Bold, a step above the semibold details (Petr: "a little stronger"). */
    *:not(i) {
      font-weight: 700 !important;
    }
  }
  .td.taskId {
    > div {
      padding: 0 5px;
    }
  }
`;

// Lines between rows at a third of the usual border, so the rows read as a list rather than a grid. The column headings
// a step lighter than the values (values in the plain secondary colour: lighter still read as disabled - Petr).
const rowLine = (theme: any) => `color-mix(in srgb, ${theme.borderColor} 35%, ${theme.background})`;
// The soft edge of what the list scrolls under (pinned columns, stuck column labels): a shadow on light; on dark a
// light tint read as a bright band, so a deeper shadow there.
export const pinnedEdge = (theme: any) =>
  theme.name === 'dark' ? 'rgba(0, 0, 0, 0.4)' : `color-mix(in srgb, ${theme.textColor} 10%, transparent)`;

// The pinned columns' side edges, lighter than the labels' (Petr: too strong on both sides).
export const pinnedSideEdge = (theme: any) =>
  theme.name === 'dark' ? 'rgba(0, 0, 0, 0.25)' : `color-mix(in srgb, ${theme.textColor} 6%, transparent)`;

export const detailText = (theme: any) => `color-mix(in srgb, ${theme.textColor} 85%, ${theme.background})`;
